import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Heart } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { API_URL, API_ORIGIN } from "../config";

/* ===== Price UI ===== */
function PriceView({ base, promo, currency="ARS" }) {
  if (base == null && promo == null) return <span>Consultar</span>;
  if (promo != null) {
    return (
      <span>
        <span className="line-through opacity-60 mr-2">${base.toLocaleString("es-AR")}</span>
        <span>${promo.toLocaleString("es-AR")}</span>
        <span className="ml-1 text-xs opacity-70">{currency}</span>
      </span>
    );
  }
  return <span>${base.toLocaleString("es-AR")} <span className="ml-1 text-xs opacity-70">{currency}</span></span>;
}

const Carousel = () => {
  const { addToCart } = useCart();
  const [items, setItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/products.php`);
        if (!resp.ok) throw new Error("Error API");
        const data = await resp.json();
        
        // Filtramos destacados o los primeros 5
        const featured = data.filter(p => p.featured);
        setItems((featured.length ? featured : data).slice(0, 5));
      } catch (e) {
        console.error("carousel fetch error", e);
      }
    })();
  }, []);

  // autoplay
  useEffect(() => {
    if (!items.length) return;
    const interval = setInterval(() => {
      setCurrentIndex((p) => (p + 1) % items.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [items.length]);

  const goToPrev = () => items.length && setCurrentIndex((p) => (p - 1 + items.length) % items.length);
  const goToNext = () => items.length && setCurrentIndex((p) => (p + 1) % items.length);

  const product = items[currentIndex];
  
  const currentVar = useMemo(() => {
    if (!product || !product.variants) return null;
    return product.variants.find(v => v.key === "18") || product.variants[0];
  }, [product]);

  const handleAdd = () => {
    if (!product || !currentVar) return;
    const finalPrice = currentVar.price_promo ?? currentVar.price_base;
    if (finalPrice == null) return;

    addToCart({
      id: `${product.id}-${currentVar.key}`,
      productId: product.id,
      name: product.name,
      detail: product.detail,
      image: product.image?.startsWith('/') ? `${API_ORIGIN}${product.image}` : product.image,
      currency: product.currency || "ARS",
      price: Number(finalPrice),
      variant: currentVar.key,
    });
  };

  if (!product) return null;

  return (
    <div className="relative h-[60vh] md:h-[70vh] overflow-hidden rounded-2xl mx-4 my-8 shadow-2xl">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0 w-full h-full bg-gradient-to-br from-pink-100 to-rose-200"
        >
          <div className="relative flex h-full w-full flex-col md:flex-row">
            {/* IMAGEN */}
            <div className="relative order-1 md:order-2 flex-1 h-[45%] md:h-auto">
              <img
                src={product.image?.startsWith('/') ? `${API_ORIGIN}${product.image}` : (product.image || '/placeholder.jpg')}
                alt={product.name}
                className="w-full h-full object-cover rounded-t-2xl md:rounded-2xl drop-shadow-2xl transition-all"
                onError={(e) => { 
                  if (!e.target.dataset.error) {
                    e.target.dataset.error = "true";
                    e.target.src = 'https://placehold.co/800x600?text=GK+Kitchen';
                  }
                }}
              />
            </div>

            {/* TEXTO / BOTONES */}
            <div className="relative z-20 order-2 md:order-1 flex-1 p-5 sm:p-6 md:p-10 lg:p-14 text-center md:text-left flex flex-col justify-center">
              <h2 className="text-2xl sm:text-4xl md:text-5xl font-extrabold text-gray-800 mb-3 md:mb-4">
                {product.name}
              </h2>
              <p className="text-sm sm:text-lg md:text-xl text-gray-700/90 mb-5 md:mb-8 max-w-xl md:max-w-none mx-auto md:mx-0 line-clamp-3">
                {product.detail}
              </p>

              <div className="flex flex-col xs:flex-row gap-3 sm:gap-4 justify-center md:justify-start">
                <button
                  onClick={handleAdd}
                  className="bg-pink-600 text-white px-6 sm:px-8 py-3 rounded-full font-semibold hover:shadow-lg transition-all"
                >
                  🛒 Agregar –{" "}
                  <PriceView
                    base={currentVar?.price_base ?? null}
                    promo={currentVar?.price_promo ?? null}
                    currency={product.currency}
                  />
                </button>

                <button className="bg-white text-pink-500 px-6 sm:px-8 py-3 rounded-full font-semibold border-2 border-pink-500 hover:bg-pink-50 transition-all flex items-center justify-center">
                  <Heart className="w-5 h-5 mr-2" />
                  Favoritos
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Flechas */}
      <button
        onClick={goToPrev}
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg z-30"
      >
        <ArrowLeft className="w-6 h-6 text-pink-500" />
      </button>
      <button
        onClick={goToNext}
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg z-30"
      >
        <ArrowRight className="w-6 h-6 text-pink-500" />
      </button>

      {/* Dots */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 z-30">
        {items.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-3 h-3 rounded-full transition-all ${
              index === currentIndex ? "bg-pink-500 scale-125" : "bg-white/60"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default Carousel;
